/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { COUPON_ADDED_PARAM } from '@/lib/cart/coupon-query';

interface CouponNotice {
    code: string;
    pathname: string;
}

/** Display the successful coupon-link result below the site header. */
export function CouponCodeBanner() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [notice, setNotice] = useState<CouponNotice | null>(() => {
        const code = searchParams.get(COUPON_ADDED_PARAM);
        return code ? { code, pathname: location.pathname } : null;
    });

    useEffect(() => {
        const code = searchParams.get(COUPON_ADDED_PARAM);
        if (code) {
            setNotice((current) =>
                current?.code === code && current.pathname === location.pathname
                    ? current
                    : { code, pathname: location.pathname }
            );

            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete(COUPON_ADDED_PARAM);
            const search = nextParams.toString();
            void navigate({ search: search ? `?${search}` : '' }, { replace: true });
            return;
        }

        setNotice((current) => (current && current.pathname !== location.pathname ? null : current));
    }, [location.pathname, navigate, searchParams]);

    if (!notice || notice.pathname !== location.pathname) return null;

    return (
        <div
            role="status"
            data-testid="coupon-code-banner"
            className="flex min-h-10 w-full items-center justify-center bg-primary px-4 py-2 text-center text-xs font-medium uppercase tracking-[0.12em] text-primary-foreground">
            Coupon {notice.code} has been applied to your cart
        </div>
    );
}
